import React from 'react';
import { FaPlus, FaTrash, FaChevronUp, FaChevronDown } from 'react-icons/fa';

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone (country code + number)' },
  { value: 'select', label: 'Dropdown' },
  { value: 'textarea', label: 'Textarea' },
];

const inputClass = 'input-modern';
const labelClass = 'text-xs font-semibold uppercase tracking-wider text-gray-500';

const slugifyKey = (label, existingKeys = []) => {
  let base = String(label || 'field')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '') || 'field';
  let key = base;
  let i = 1;
  while (existingKeys.includes(key)) {
    key = `${base}_${i}`;
    i += 1;
  }
  return key;
};

const WebinarRegistrationFormEditor = ({ form, setForm }) => {
  const registrationForm = form.registrationForm || { fields: [], countryCodes: [] };

  const updateRegistration = (field, value) => {
    setForm((prev) => ({
      ...prev,
      registrationForm: { ...prev.registrationForm, [field]: value },
    }));
  };

  const mutateFields = (mutator) => {
    setForm((prev) => {
      const fields = mutator([...(prev.registrationForm?.fields || [])]);
      return {
        ...prev,
        registrationForm: {
          ...prev.registrationForm,
          fields: fields.map((field, index) => ({ ...field, order: index })),
        },
      };
    });
  };

  const updateField = (index, field, value) => {
    mutateFields((fields) => {
      fields[index] = { ...fields[index], [field]: value };
      return fields;
    });
  };

  const addField = () => {
    mutateFields((fields) => {
      const keys = fields.map((f) => f.key);
      fields.push({
        key: slugifyKey(`custom_field_${fields.length + 1}`, keys),
        label: 'New Field',
        type: 'text',
        required: false,
        placeholder: '',
        order: fields.length,
        isActive: true,
        options: [],
      });
      return fields;
    });
  };

  const removeField = (index) => {
    mutateFields((fields) => fields.filter((_, i) => i !== index));
  };

  const moveField = (index, direction) => {
    mutateFields((fields) => {
      const next = index + direction;
      if (next < 0 || next >= fields.length) return fields;
      [fields[index], fields[next]] = [fields[next], fields[index]];
      return fields;
    });
  };

  const updateOption = (fieldIndex, optionIndex, key, value) => {
    mutateFields((fields) => {
      const options = [...(fields[fieldIndex]?.options || [])];
      options[optionIndex] = { ...options[optionIndex], [key]: value };
      fields[fieldIndex] = { ...fields[fieldIndex], options };
      return fields;
    });
  };

  const addOption = (fieldIndex) => {
    mutateFields((fields) => {
      const options = [...(fields[fieldIndex]?.options || []), { value: '', label: '' }];
      fields[fieldIndex] = { ...fields[fieldIndex], options };
      return fields;
    });
  };

  const removeOption = (fieldIndex, optionIndex) => {
    mutateFields((fields) => {
      const options = (fields[fieldIndex]?.options || []).filter((_, i) => i !== optionIndex);
      fields[fieldIndex] = { ...fields[fieldIndex], options };
      return fields;
    });
  };

  const updateCountryCode = (index, key, value) => {
    setForm((prev) => {
      const countryCodes = [...(prev.registrationForm?.countryCodes || [])];
      countryCodes[index] = { ...countryCodes[index], [key]: value };
      return {
        ...prev,
        registrationForm: { ...prev.registrationForm, countryCodes },
      };
    });
  };

  const addCountryCode = () => {
    setForm((prev) => ({
      ...prev,
      registrationForm: {
        ...prev.registrationForm,
        countryCodes: [...(prev.registrationForm?.countryCodes || []), { value: '', label: '' }],
      },
    }));
  };

  const removeCountryCode = (index) => {
    setForm((prev) => ({
      ...prev,
      registrationForm: {
        ...prev.registrationForm,
        countryCodes: (prev.registrationForm?.countryCodes || []).filter((_, i) => i !== index),
      },
    }));
  };

  return (
    <div className="space-y-6">
      <div className="p-3 rounded-xl bg-blue-50 dark:bg-blue-900/20 text-sm text-blue-800 dark:text-blue-200">
        Edit registration form labels, fields, dropdown options, and success/WhatsApp messages.
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Section title</label>
          <input className={inputClass} value={registrationForm.sectionTitle || ''} onChange={(e) => updateRegistration('sectionTitle', e.target.value)} />
        </div>
        <div>
          <label className={labelClass}>Highlighted word</label>
          <input className={inputClass} value={registrationForm.sectionTitleHighlight || ''} onChange={(e) => updateRegistration('sectionTitleHighlight', e.target.value)} />
        </div>
        <div className="md:col-span-2">
          <label className={labelClass}>Section subtitle</label>
          <input className={inputClass} value={registrationForm.sectionSubtitle || ''} onChange={(e) => updateRegistration('sectionSubtitle', e.target.value)} />
        </div>
        <div>
          <label className={labelClass}>Submit button</label>
          <input className={inputClass} value={registrationForm.submitLabel || ''} onChange={(e) => updateRegistration('submitLabel', e.target.value)} />
        </div>
        <div>
          <label className={labelClass}>Submitting label</label>
          <input className={inputClass} value={registrationForm.submittingLabel || ''} onChange={(e) => updateRegistration('submittingLabel', e.target.value)} />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 border-t border-gray-100 dark:border-dark-700 pt-4">
        <h3 className="text-sm font-bold text-gray-800 dark:text-gray-200">Success Message</h3>
        <div>
          <label className={labelClass}>Success title</label>
          <input className={inputClass} value={registrationForm.successTitle || ''} onChange={(e) => updateRegistration('successTitle', e.target.value)} />
        </div>
        <div>
          <label className={labelClass}>Success message</label>
          <textarea className={`${inputClass} min-h-[80px]`} value={registrationForm.successMessage || ''} onChange={(e) => updateRegistration('successMessage', e.target.value)} />
        </div>
        <div>
          <label className={labelClass}>WhatsApp block title</label>
          <input className={inputClass} value={registrationForm.whatsappTitle || ''} onChange={(e) => updateRegistration('whatsappTitle', e.target.value)} />
        </div>
        <div>
          <label className={labelClass}>WhatsApp message</label>
          <textarea className={`${inputClass} min-h-[100px]`} value={registrationForm.whatsappMessage || ''} onChange={(e) => updateRegistration('whatsappMessage', e.target.value)} />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>WhatsApp link</label>
            <input className={inputClass} value={registrationForm.whatsappLink || ''} onChange={(e) => updateRegistration('whatsappLink', e.target.value)} />
          </div>
          <div>
            <label className={labelClass}>WhatsApp button label</label>
            <input className={inputClass} value={registrationForm.whatsappButtonLabel || ''} onChange={(e) => updateRegistration('whatsappButtonLabel', e.target.value)} />
          </div>
        </div>
        <div>
          <label className={labelClass}>WhatsApp footer</label>
          <textarea className={`${inputClass} min-h-[60px]`} value={registrationForm.whatsappFooter || ''} onChange={(e) => updateRegistration('whatsappFooter', e.target.value)} />
        </div>
        <div>
          <label className={labelClass}>Error message</label>
          <input className={inputClass} value={registrationForm.errorMessage || ''} onChange={(e) => updateRegistration('errorMessage', e.target.value)} />
        </div>
      </div>

      <div className="border-t border-gray-100 dark:border-dark-700 pt-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-gray-800 dark:text-gray-200">Country Codes (for phone field)</h3>
          <button type="button" onClick={addCountryCode} className="text-sm text-primary-600 font-semibold flex items-center gap-1">
            <FaPlus className="w-3 h-3" /> Add Code
          </button>
        </div>
        {(registrationForm.countryCodes || []).map((code, index) => (
          <div key={`code-${index}`} className="grid md:grid-cols-3 gap-2 items-end">
            <input className={inputClass} value={code.value || ''} placeholder="Value e.g. +91" onChange={(e) => updateCountryCode(index, 'value', e.target.value)} />
            <input className={inputClass} value={code.label || ''} placeholder="Label e.g. +91 (IN)" onChange={(e) => updateCountryCode(index, 'label', e.target.value)} />
            <button type="button" onClick={() => removeCountryCode(index)} className="p-2 text-red-500 border rounded-xl">
              <FaTrash />
            </button>
          </div>
        ))}
      </div>

      <div className="border-t border-gray-100 dark:border-dark-700 pt-4 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-gray-800 dark:text-gray-200">Form Fields</h3>
          <button type="button" onClick={addField} className="text-sm text-primary-600 font-semibold flex items-center gap-1">
            <FaPlus className="w-3 h-3" /> Add Field
          </button>
        </div>

        {(registrationForm.fields || []).map((field, fieldIndex) => (
          <div key={`${field.key}-${fieldIndex}`} className="rounded-2xl border border-gray-200 dark:border-dark-600 p-4 space-y-3 bg-gray-50/40 dark:bg-dark-900/20">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-bold text-gray-700 dark:text-gray-200">Field {fieldIndex + 1}</span>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => moveField(fieldIndex, -1)} className="p-1.5 border rounded"><FaChevronUp className="w-3 h-3" /></button>
                <button type="button" onClick={() => moveField(fieldIndex, 1)} className="p-1.5 border rounded"><FaChevronDown className="w-3 h-3" /></button>
                <button type="button" onClick={() => removeField(fieldIndex)} className="p-1.5 border rounded text-red-500"><FaTrash className="w-3 h-3" /></button>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Label</label>
                <input className={inputClass} value={field.label || ''} onChange={(e) => updateField(fieldIndex, 'label', e.target.value)} />
              </div>
              <div>
                <label className={labelClass}>Field key</label>
                <input className={inputClass} value={field.key || ''} onChange={(e) => updateField(fieldIndex, 'key', e.target.value.replace(/\s+/g, '_'))} />
              </div>
              <div>
                <label className={labelClass}>Type</label>
                <select className={inputClass} value={field.type || 'text'} onChange={(e) => updateField(fieldIndex, 'type', e.target.value)}>
                  {FIELD_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Placeholder</label>
                <input className={inputClass} value={field.placeholder || ''} onChange={(e) => updateField(fieldIndex, 'placeholder', e.target.value)} />
              </div>
            </div>

            <div className="flex flex-wrap gap-4 text-xs">
              <label className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${field.required ? 'border-amber-200 bg-amber-50' : 'border-gray-200'}`}>
                <input type="checkbox" checked={field.required === true} onChange={(e) => updateField(fieldIndex, 'required', e.target.checked)} />
                Required
              </label>
              <label className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${field.isActive !== false ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
                <input type="checkbox" checked={field.isActive !== false} onChange={(e) => updateField(fieldIndex, 'isActive', e.target.checked)} />
                {field.isActive !== false ? 'Active (shown on form)' : 'Disabled (hidden)'}
              </label>
            </div>

            {field.type === 'select' && (
              <div className="space-y-2 border-t border-gray-100 dark:border-dark-700 pt-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase text-gray-500">Dropdown options</span>
                  <button type="button" onClick={() => addOption(fieldIndex)} className="text-xs text-primary-600 font-semibold flex items-center gap-1">
                    <FaPlus /> Option
                  </button>
                </div>
                {(field.options || []).map((option, optionIndex) => (
                  <div key={`opt-${optionIndex}`} className="grid md:grid-cols-3 gap-2 items-end">
                    <input className={inputClass} value={option.value || ''} placeholder="Value" onChange={(e) => updateOption(fieldIndex, optionIndex, 'value', e.target.value)} />
                    <input className={inputClass} value={option.label || ''} placeholder="Label" onChange={(e) => updateOption(fieldIndex, optionIndex, 'label', e.target.value)} />
                    <button type="button" onClick={() => removeOption(fieldIndex, optionIndex)} className="p-2 text-red-500 border rounded-xl">
                      <FaTrash />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default WebinarRegistrationFormEditor;
